/**
 * Content-shaped skeleton placeholders.
 *
 * Replace plain Spinner blocks where we know the layout shape in advance.
 * Kids + parents perceive skeletons as "content is coming" rather than
 * "something is broken"; layout shift when real content arrives is also
 * minimised.
 *
 * Every skeleton block shares the `huroof-shimmer` animation (defined in
 * styles/index.css) — a soft left-to-right sheen on a sand-tinted base.
 */

/** Low-level rectangle. Use for custom layouts. */
export function SkeletonBlock({
  class: extra,
  round = 'rounded-2xl',
}: {
  class?: string;
  round?: string;
}) {
  return (
    <div
      class={`huroof-shimmer bg-ink/5 ${round} ${extra ?? ''}`}
      aria-hidden="true"
    />
  );
}

/** Skeleton for the Island / stage-select screen. */
export function IslandSkeleton() {
  return (
    <div
      class="flex-1 flex flex-col items-center px-4 py-6 pb-24 max-w-md mx-auto w-full gap-6"
      aria-hidden="true"
    >
      <SkeletonBlock class="h-6 w-24 self-start" round="rounded-md" />

      {/* Big letter placeholder */}
      <div class="flex flex-col items-center gap-3">
        <SkeletonBlock class="h-48 w-48" round="rounded-full" />
        <SkeletonBlock class="h-6 w-24" round="rounded-md" />
        <SkeletonBlock class="h-3 w-16" round="rounded-md" />
      </div>

      {/* Two audio buttons */}
      <div class="flex gap-3">
        <SkeletonBlock class="h-14 w-24" />
        <SkeletonBlock class="h-14 w-24" />
      </div>

      {/* Five stage-button placeholders, staggered like the real layout */}
      <div class="w-full space-y-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{ marginInlineStart: `${i * 16}px` }}
          >
            <SkeletonBlock class="h-20 w-full" round="rounded-3xl" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for the World Map (letter tile grid). */
export function MapSkeleton() {
  return (
    <div
      class="flex-1 px-4 py-6 pb-24 max-w-3xl mx-auto w-full"
      aria-hidden="true"
    >
      <header class="flex items-center justify-between mb-6 px-2">
        <div class="space-y-1">
          <SkeletonBlock class="h-7 w-28" round="rounded-md" />
          <SkeletonBlock class="h-3 w-40" round="rounded-md" />
        </div>
        <div class="flex gap-2">
          <SkeletonBlock class="h-7 w-14" round="rounded-full" />
          <SkeletonBlock class="h-7 w-14" round="rounded-full" />
        </div>
      </header>

      {/* Three group sections, each with 4 tile placeholders */}
      {[0, 1, 2].map((gi) => (
        <section key={gi} class="mb-6">
          <SkeletonBlock class="h-3 w-24 mb-2" round="rounded-md" />
          <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((ti) => (
              <SkeletonBlock key={ti} class="aspect-square" round="rounded-[28px]" />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Skeleton for the Letter Collection Book (grid of tiles). */
export function CollectionSkeleton() {
  return (
    <div
      class="flex-1 px-4 py-6 pb-24 max-w-3xl mx-auto w-full"
      aria-hidden="true"
    >
      <header class="flex items-center justify-between mb-6 px-2">
        <SkeletonBlock class="h-6 w-16" round="rounded-md" />
        <SkeletonBlock class="h-7 w-40" round="rounded-md" />
      </header>
      <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <SkeletonBlock key={i} class="aspect-square" round="rounded-3xl" />
        ))}
      </div>
    </div>
  );
}
